// جدول Audit پلتفرم (US-PLT-083) با جزئیات قبل و بعد؛ در SCR-CONSOLE-009 و تب Audit جزئیات مستأجر (SCR-CONSOLE-002).
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type PlatformAuditEventView } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, JalaliDate, KeyValueList, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';
import { consoleKeys } from '../../api';

export interface AuditFilters {
  tenantId?: string;
  action?: string;
  actorId?: string;
  /** ISO 8601 (UTC). */
  from?: string;
  to?: string;
}

const PAGE_SIZE = 50;

/** نتیجه Audit (AuditResult: Success، Denied، Failed). */
function auditResult(value: string): 'success' | 'denied' | 'failed' {
  const normalized = value.toLowerCase();
  return normalized === 'success' || normalized === 'denied' ? normalized : 'failed';
}

function json(value: unknown): string {
  return value === null || value === undefined ? '' : JSON.stringify(value, null, 2);
}

function DiffPane({
  titleKey,
  value,
  changed,
}: {
  titleKey: string;
  value: unknown;
  changed: readonly string[];
}) {
  const { t } = useTranslation('console');
  const entries =
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.entries(value as Record<string, unknown>)
      : undefined;
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t(titleKey)}
      </Typography>
      <Box
        component="pre"
        dir="ltr"
        sx={{
          m: 0,
          p: 1,
          bgcolor: 'action.hover',
          borderRadius: 1,
          fontSize: 12,
          overflow: 'auto',
          maxHeight: 320,
        }}
      >
        {entries
          ? entries.map(([key, item]) => (
              <Box
                key={key}
                component="span"
                data-changed={changed.includes(key) ? 'true' : undefined}
                sx={
                  changed.includes(key)
                    ? { display: 'block', bgcolor: 'warning.light', color: 'warning.contrastText' }
                    : { display: 'block' }
                }
              >
                {`${key}: ${JSON.stringify(item)}`}
              </Box>
            ))
          : json(value) || t('common:states.none')}
      </Box>
    </Box>
  );
}

function AuditDetailDialog({
  event,
  onClose,
}: {
  event: PlatformAuditEventView | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('console');
  const titleId = useId();
  return (
    <Dialog open={event !== null} onClose={onClose} aria-labelledby={titleId} maxWidth="md" fullWidth>
      <DialogTitle id={titleId}>{t('console:audit.detail_title')}</DialogTitle>
      {event && (
        <DialogContent>
          <Stack spacing={2}>
            <KeyValueList
              items={[
                {
                  labelKey: 'console:audit.columns.time',
                  value: <JalaliDate value={event.occurredAt} withTime />,
                },
                { labelKey: 'console:audit.columns.action', value: <bdi dir="ltr">{event.action}</bdi> },
                {
                  labelKey: 'console:audit.columns.resource',
                  value: (
                    <bdi dir="ltr">
                      {event.resourceType}
                      {event.resourceId ? ` / ${event.resourceId}` : ''}
                    </bdi>
                  ),
                },
                {
                  labelKey: 'console:audit.columns.tenant',
                  value: event.tenantId && <bdi dir="ltr">{event.tenantId}</bdi>,
                },
                {
                  labelKey: 'console:audit.columns.actor',
                  value: event.actorId && <bdi dir="ltr">{event.actorId}</bdi>,
                },
                {
                  labelKey: 'console:audit.columns.result',
                  value: t(`console:audit.result.${auditResult(event.result)}`),
                },
                { labelKey: 'console:audit.columns.reason', value: event.reason },
                {
                  labelKey: 'console:audit.correlation_id',
                  value: event.correlationId && <bdi dir="ltr">{event.correlationId}</bdi>,
                },
              ]}
            />
            {event.changedFields.length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                data-testid="audit-changed-fields"
              >
                <Typography variant="caption" color="text.secondary" sx={{ me: 1 }}>
                  {t('console:audit.changed_fields')}
                </Typography>
                {event.changedFields.map((field) => (
                  <Chip key={field} label={field} dir="ltr" variant="outlined" />
                ))}
              </Stack>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} data-testid="audit-diff">
              <DiffPane titleKey="console:audit.before" value={event.before} changed={event.changedFields} />
              <DiffPane titleKey="console:audit.after" value={event.after} changed={event.changedFields} />
            </Stack>
          </Stack>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function AuditTable({
  filters,
  toolbar,
  showTenant = true,
}: {
  filters: AuditFilters;
  toolbar?: ReactNode;
  showTenant?: boolean;
}) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const [selected, setSelected] = useState<PlatformAuditEventView | null>(null);

  const events = useInfiniteQuery({
    queryKey: consoleKeys.audit({ ...filters }),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/api/v1/platform/console/audit-events', {
          params: { query: { ...filters, cursor: pageParam, limit: PAGE_SIZE } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const columns: DataTableColumn<PlatformAuditEventView>[] = [
    {
      id: 'time',
      headerKey: 'console:audit.columns.time',
      cell: (row) => <JalaliDate value={row.occurredAt} withTime />,
    },
    {
      id: 'action',
      headerKey: 'console:audit.columns.action',
      cell: (row) => <bdi dir="ltr">{row.action}</bdi>,
    },
    {
      id: 'resource',
      headerKey: 'console:audit.columns.resource',
      cell: (row) => <bdi dir="ltr">{row.resourceType}</bdi>,
    },
    ...(showTenant
      ? [
          {
            id: 'tenant',
            headerKey: 'console:audit.columns.tenant',
            cell: (row: PlatformAuditEventView) =>
              row.tenantId ? <bdi dir="ltr">{row.tenantId.slice(-12)}</bdi> : t('common:states.none'),
          },
        ]
      : []),
    {
      id: 'result',
      headerKey: 'console:audit.columns.result',
      cell: (row) => {
        const result = auditResult(row.result);
        return (
          <StatusChip
            tone={result === 'success' ? 'active' : 'error'}
            labelKey={`console:audit.result.${result}`}
          />
        );
      },
    },
    {
      id: 'reason',
      headerKey: 'console:audit.columns.reason',
      cell: (row) => row.reason ?? t('common:states.none'),
    },
  ];

  return (
    <>
      <DataTable
        captionKey="console:audit.caption"
        columns={columns}
        rows={events.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.eventId}
        isLoading={events.isPending}
        error={events.error}
        onRetry={() => void events.refetch()}
        hasMore={events.hasNextPage}
        onLoadMore={() => void events.fetchNextPage()}
        isFetchingMore={events.isFetchingNextPage}
        onRowClick={setSelected}
        toolbar={toolbar}
        emptyTitleKey="console:audit.empty_title"
      />
      <AuditDetailDialog event={selected} onClose={() => setSelected(null)} />
    </>
  );
}
